import { get } from "./apiService";
import type {
  MarketAsset,
  PriceHistoryPoint,
  PriceHistoryResponse,
} from "../types/api";

type MarketAssetsResponse = Record<string, MarketAsset>;

interface MarketChartOhlcvPoint {
  time_open?: string;
  time_close?: string;
  close?: number | string | null;
}

interface MarketChartFallbackResponse {
  symbol: string;
  interval: string;
  ohlcv?: MarketChartOhlcvPoint[];
}

interface CacheEntry<T> {
  expiresAt: number;
  value: T;
}

const MARKET_HISTORY_CACHE_TTL_MS = 5 * 60 * 1000;
const priceHistoryCache = new Map<
  string,
  CacheEntry<{ data: PriceHistoryResponse }>
>();
const priceHistoryInFlight = new Map<
  string,
  Promise<{ data: PriceHistoryResponse }>
>();

const getCachedMarketRequest = async <T>(
  cache: Map<string, CacheEntry<T>>,
  inFlight: Map<string, Promise<T>>,
  key: string,
  request: () => Promise<T>
): Promise<T> => {
  const cached = cache.get(key);

  if (cached && cached.expiresAt > Date.now()) {
    return cached.value;
  }

  const pending = inFlight.get(key);
  if (pending) {
    return pending;
  }

  const nextRequest = request()
    .then((value) => {
      cache.set(key, {
        expiresAt: Date.now() + MARKET_HISTORY_CACHE_TTL_MS,
        value,
      });
      return value;
    })
    .finally(() => {
      inFlight.delete(key);
    });

  inFlight.set(key, nextRequest);
  return nextRequest;
};

// ─── Market Data ────────────────────────────────────────────────────────────

/**
 * Get market data for all listed assets.
 * Endpoint is already v2 and unchanged.
 */
export const getMarketAssets = async (): Promise<{
  data: MarketAssetsResponse;
}> => {
  try {
    const result = await get<MarketAssetsResponse>("/api/v2/market/allInfo");
    return {
      data: result,
    };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    throw new Error(`Could not get market assets: ${message}`);
  }
};

// ─── Price History ──────────────────────────────────────────────────────────

/**
 * Get price history for a given asset symbol.
 * Endpoint is already v2 and unchanged.
 *
 * @param symbol  - Asset symbol (e.g. "BTC")
 * @param interval - Candle interval ("1h", "1d", "1w", etc.)
 * @param range   - Optional time range ("1d", "1w", "1m", "3m", "1y", "all")
 */
export const getPriceHistory = async (
  symbol: string,
  interval?: string,
  range?: string
): Promise<{ data: PriceHistoryResponse }> => {
  if (!symbol) {
    return { data: { success: false, data: null } };
  }

  const normalizedSymbol = symbol.trim().toUpperCase();
  const cacheKey = `${normalizedSymbol}:${interval || "1d"}:${range ?? ""}`;
  const params: Record<string, string> = {
    interval: interval || "1d",
  };
  if (range) params.range = range;

  return getCachedMarketRequest(
    priceHistoryCache,
    priceHistoryInFlight,
    cacheKey,
    async () => {
      const getChartsFallback = async (): Promise<PriceHistoryPoint[]> => {
        const fallback = await get<MarketChartFallbackResponse>(
          `/api/v2/market/${encodeURIComponent(normalizedSymbol)}/charts`,
          params
        );

        return (fallback.ohlcv ?? []).flatMap((point) => {
          const close =
            typeof point.close === "number"
              ? point.close
              : typeof point.close === "string"
                ? Number(point.close)
                : null;
          const timestamp = point.time_open ?? point.time_close;

          if (close === null || !Number.isFinite(close) || !timestamp) {
            return [];
          }

          return [
            {
              timestamp,
              open: close,
              high: close,
              low: close,
              close,
            },
          ];
        });
      };

      try {
        // V2 ENDPOINT: GET /api/v2/market/:symbol/priceHistory
        const result = await get<PriceHistoryResponse["data"]>(
          `/api/v2/market/${encodeURIComponent(normalizedSymbol)}/priceHistory`,
          params
        );

        if (!result?.length) {
          const fallbackData = await getChartsFallback();
          if (fallbackData.length > 0) {
            return {
              data: {
                success: true,
                data: fallbackData,
              },
            };
          }
        }

        return {
          data: {
            success: true,
            data: result,
          },
        };
      } catch (err: unknown) {
        try {
          const fallbackData = await getChartsFallback();
          if (fallbackData.length > 0) {
            return {
              data: {
                success: true,
                data: fallbackData,
              },
            };
          }
        } catch {
          // Preserve the primary endpoint error so callers see the original failure.
        }

        const message = err instanceof Error ? err.message : String(err);
        throw new Error(`Could not get price history: ${message}`);
      }
    }
  );
};
